<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BuildingCanvasDialogModal from '@/components/dialogs/BuildingCanvasDialogModal.vue';
import BuildingDatatable from '@/components/tables/BuildingDatatable.vue';
import DialogModal from '@/components/dialogs/DialogModal.vue';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import ProjectRenderCanvasAccessible from '@/components/maps_canvases/ProjectRenderCanvasAccessible.vue';
import BuildingList from '@/components/rental/buildings/BuildingList.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import CanvasDataPanels from '@/components/utilities/CanvasDataPanels.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import ViewModeToggleButton from '@/components/buttons/ViewModeToggleButton.vue';
import { useBuildingsApi } from '@/composables/useBuildingsApi';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { useRentalFilters } from '@/composables/useRentalFilters';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useProjectsApi } from '@/composables/useProjectsApi';
import { ListingTypeEnum as ListingTypeEnumValues, listingTypeEnumNumericValue } from '@/enums/ListingType';
import type { LocaleSchema } from '@/locales/index';
import type { Building } from '@/typings/buildings/Building';
import type { BuildingRenderLayer } from '@/typings/drawing/BuildingRenderLayer';
import type { Project } from '@/typings/projects/Project';
import type { GetBuildingsRequest } from '@/typings/requests/GetBuildingsRequest.ts';
import type { GetProjectRenderCanvasRequest } from '@/typings/requests/GetProjectRenderCanvasRequest.ts';
import type { Unit } from '@/typings/units/Unit';
import type { Region } from '@/typings/regions/Region';
import { DEFAULT_RENTAL_UNIT_STATUSES } from '@/constants/rentalConstants';
import { DEFAULT_PURCHASE_UNIT_STATUSES } from '@/constants/purchaseConstants';
import { InlineSearchBar } from '@/components/utilities/search';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const { getProjectRenderCanvas, getProjectRenderCanvasLoading } = useProjectsApi();
const { getBuildingsByProjectId, getBuildingsByProjectIdLoading } = useBuildingsApi();
const { getRegionWizardUrlFromProject, getBuildingWizardUrl: getRentalBuildingWizardUrl } = useRentalLocalizedUrls();
const { getBuildingWizardUrl: getPurchaseBuildingWizardUrl } = usePurchaseLocalizedUrls();
const { loadFiltersFromRentalStorage } = useRentalFilters();

const props = defineProps<{
  project: Project;
  region?: Region;
}>();

// Mode d'affichage (cards ou table)
const VIEW_MODE_STORAGE_KEY = 'rental-project-browsing-view-mode';
const viewMode = ref<'cards' | 'table'>('table');

// Sauvegarder le mode d'affichage dans localStorage quand il change
watch(viewMode, (newMode) => {
  localStorage.setItem(VIEW_MODE_STORAGE_KEY, newMode);
});

// Détection de la largeur d'écran pour afficher le bon bouton
const isDesktop = ref(window.innerWidth > 768);

const hoveredBuilding = ref<Building>();
const canvasIsLoaded = ref(false);
const buildingLayers = ref<BuildingRenderLayer[]>([]);
const buildings = ref<Building[]>([]);
const allBuildings = ref<Building[]>([]);
const saleBuildings = ref<Building[]>([]);
const saleBuildingLayers = ref<BuildingRenderLayer[]>([]);
const hoveredUnit = ref<Unit>();
const project = ref<Project>(props.project);
const wizardStep = ref(3);

// Modal state
const showBuildingModal = ref(false);
const modalBuilding = ref<Building>();

// Purchase notification modal state
const showPurchaseNotificationModal = ref(false);
const purchaseBuilding = ref<Building>();

const canvasIsReady = computed(() => {
  return buildingLayers.value.length > 0 && canvasIsLoaded.value;
});

// Variables pour l'affichage progressif des bâtiments
const INITIAL_BUILDINGS_COUNT = 18;
const displayedBuildingsCount = ref(INITIAL_BUILDINGS_COUNT);

const remainingBuildingsCount = computed(() => Math.max(0, buildings.value.length - displayedBuildingsCount.value));
const hasMoreBuildings = computed(() => remainingBuildingsCount.value > 0);

const displayedBuildings = computed(() => {
  return buildings.value.slice(0, displayedBuildingsCount.value).sort((a, b) => {
    // Trier par numéro de bâtiment
    return a.no.localeCompare(b.no, locale.value);
  });
});

// Computed pour obtenir l'ID de région du projet
const projectRegionId = computed(() => {
  // D'abord essayer depuis les props
  if (props.region?.id) {
    return props.region.id;
  }
  
  // Sinon, essayer de récupérer depuis les filtres stockés
  const filters = loadFiltersFromRentalStorage();
  return filters.regionId || null;
});

const loadMoreBuildings = () => {
  displayedBuildingsCount.value = buildings.value.length;
};

const headerTitle = computed(() => {
  return t('browsingApp.buildings.chooseTheBuildingListDescription', { 
    count: buildings.value.length, 
    project: props.project.localizedName 
  });
});

function onCanvasLoaded() {
  canvasIsLoaded.value = true;
}

function onLayerClick(layer: BuildingRenderLayer) {
  const building = allBuildings.value.find(b => b.id === layer.id);
  if (building) {
    // Si c'est un bâtiment de vente, afficher le popup de notification d'abord
    if (building.listingType === ListingTypeEnumValues.Sale) {
      purchaseBuilding.value = building;
      showPurchaseNotificationModal.value = true;
      return;
    }
    
    // Sinon, ouvrir le modal pour les bâtiments en location
    modalBuilding.value = building;
    showBuildingModal.value = true;
  }
}

function onBuildingSelected(building: Building) {
  // Navigation directe vers l'étape suivante
  window.location.href = getRentalBuildingWizardUrl(building);
}

function onBackToProjectSelection() {
  window.location.href = getRegionWizardUrlFromProject(props.project);
}

function resetDialogState() {
  modalBuilding.value = undefined;
}

function onConfirmPurchaseRedirect() {
  if (purchaseBuilding.value) {
    window.location.href = getPurchaseBuildingWizardUrl(purchaseBuilding.value);
  }
}

function onCancelPurchaseRedirect() {
  showPurchaseNotificationModal.value = false;
  purchaseBuilding.value = undefined;
}

// Fonction pour appliquer les filtres et recharger les bâtiments
async function applyFilters() {
  if (project.value) {
    try {
      const filters = loadFiltersFromRentalStorage();
      
      const rentalRequest: GetBuildingsRequest = {
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Rental],
        locale: locale.value.substring(0, 2),
        unitStatuses: DEFAULT_RENTAL_UNIT_STATUSES,
        availabilityDates: filters.availabilityDates || undefined,
        dogAllowed: filters.dogAllowed ?? undefined,
        floorNumbers: filters.floors,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        unitConfigurations:  filters.unitConfigurations.length > 0 ? filters.unitConfigurations : undefined
      };

      const rentalCanvasRequest: GetProjectRenderCanvasRequest = {
        locale: locale.value.substring(0, 2),
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Rental]
      };
      
      const [rentalBuildingLayersData, filteredBuildings] = await Promise.all([
        getProjectRenderCanvas(project.value.id, rentalCanvasRequest),
        getBuildingsByProjectId(project.value.id, rentalRequest)
      ]);
      
      // Filtrer les couches de rendu pour ne montrer que les bâtiments avec unités disponibles
      const availableBuildingIds = filteredBuildings.map(b => b.id);
      const filteredRentalLayers = rentalBuildingLayersData.filter(layer => availableBuildingIds.includes(layer.id));
      
      // Combiner les couches de location filtrées avec les couches de vente (non filtrées)
      buildingLayers.value = [...filteredRentalLayers, ...saleBuildingLayers.value];
      
      // Combiner les bâtiments de location filtrés avec les bâtiments de vente (pour les clics sur le canvas)
      allBuildings.value = [...filteredBuildings, ...saleBuildings.value];
      buildings.value = filteredBuildings;
      
      // Réinitialiser le compteur de bâtiments affichés
      displayedBuildingsCount.value = INITIAL_BUILDINGS_COUNT;
    } catch (error) {
      console.error('Failed to reload buildings with filters:', error);
    }
  }
}

onMounted(async () => {
  // Écouter les changements de taille d'écran
  const handleResize = () => {
    isDesktop.value = window.innerWidth > 768;
  };
  window.addEventListener('resize', handleResize);

  if (project.value) {
    try {
      // Charger les filtres existants pour le chargement initial
      const filters = loadFiltersFromRentalStorage();
      
      const saleRequest: GetBuildingsRequest = {
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Sale],
        locale: locale.value.substring(0, 2),
        unitStatuses: DEFAULT_PURCHASE_UNIT_STATUSES,
        dogAllowed: filters.dogAllowed ?? undefined
      };

      const rentalRequest: GetBuildingsRequest = {
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Rental],
        locale: locale.value.substring(0, 2),
        unitStatuses: DEFAULT_RENTAL_UNIT_STATUSES,
        availabilityDates: filters.availabilityDates || undefined,
        dogAllowed: filters.dogAllowed ?? undefined
      };

      const saleCanvasRequest: GetProjectRenderCanvasRequest = {
        locale: locale.value.substring(0, 2),
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Sale]
      };

      const rentalCanvasRequest: GetProjectRenderCanvasRequest = {
        locale: locale.value.substring(0, 2),
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Rental]
      };

      const [saleBuildingLayersData, rentalBuildingLayersData, saleBuildingsData, rentalBuildingsData] = await Promise.all([
        getProjectRenderCanvas(project.value.id, saleCanvasRequest),
        getProjectRenderCanvas(project.value.id, rentalCanvasRequest),
        getBuildingsByProjectId(project.value.id, saleRequest),
        getBuildingsByProjectId(project.value.id, rentalRequest)
      ]);
      
      // Créer la liste de tous les IDs de bâtiments avec unités disponibles
      const availableBuildingIds = [...rentalBuildingsData, ...saleBuildingsData].map(b => b.id);
      
      // Filtrer les couches de rendu pour ne montrer que les bâtiments avec unités disponibles
      const filteredRentalLayers = rentalBuildingLayersData.filter(layer => availableBuildingIds.includes(layer.id));
      const filteredSaleLayers = saleBuildingLayersData.filter(layer => availableBuildingIds.includes(layer.id));
      
      buildingLayers.value = [...filteredRentalLayers, ...filteredSaleLayers];
      
      // Stocker les bâtiments de vente séparément (ne changent pas avec les filtres)
      saleBuildings.value = saleBuildingsData;
      saleBuildingLayers.value = filteredSaleLayers;
      
      // Stocker tous les bâtiments pour le mapping des clics
      allBuildings.value = [...rentalBuildingsData, ...saleBuildingsData];
      
      // Afficher seulement les bâtiments en location dans le datatable (déjà filtrés côté serveur)
      buildings.value = rentalBuildingsData;
      
      // Réinitialiser le compteur de bâtiments affichés
      displayedBuildingsCount.value = INITIAL_BUILDINGS_COUNT;
      
    } catch (error) {
      console.error('Failed to load project data:', error);
    }
  }
});
</script>

<template>
  <!--Location - Étape 3 - Choix du bâtiment-->
  <div class="project-rental-browsing">
    <!-- Barre de filtres -->
    <InlineSearchBar action-type="filter" 
                     :selected-region="projectRegionId"
                     @filtersApplied="applyFilters" />
    <div class="wrapper wrapper_large">
      <!-- Étapes du wizard -->
      <UnitSelectionWizardSteps v-model="wizardStep" />
      <!-- Contenu principal -->
      <div class="main-content">
        <div class="heading-and-toggle">
          <!--En-tête avec bouton de retour-->
          <HeaderWithBackButton :title="headerTitle"
                                :subtitle="t('browsingApp.buildings.chooseTheBuildingDescription')"
                                @back-click="onBackToProjectSelection">
            <template v-if="isDesktop" #actions>
              <!-- Toggle pour changer le mode d'affichage -->
              <ViewModeToggleButton v-model="viewMode" />
            </template>
          </HeaderWithBackButton>
        </div>
        <!-- Liste des bâtiments -->
        <CanvasDataPanels>
          <template #data>
            <div v-if="getBuildingsByProjectIdLoading"
                 class="browsingApp-loadingState">
              <LoadingSpinner />
            </div>

            <template v-else-if="buildings?.length">
              <!-- Mode cartes -->
              <BuildingList v-if="viewMode === 'cards'"
                            :buildings="displayedBuildings"
                            :loading="getBuildingsByProjectIdLoading"
                            :is-selectable="false"
                            :mode="'rental'"
                            @building-selected="onBuildingSelected" />

              <!-- Mode tableau -->
              <BuildingDatatable v-else
                                 mode="rental"
                                 v-model:hovered="hoveredBuilding"
                                 :buildings="displayedBuildings"
                                 @building-click="onBuildingSelected" />

              <!-- Bouton "Voir plus de bâtiments" -->
              <div v-if="hasMoreBuildings" class="loadMoreBuildings">
                <button type="button"
                        class="btn"
                        @click="loadMoreBuildings">
                  {{ t('browsingApp.buildings.loadMoreBuildings', { count: remainingBuildingsCount }, remainingBuildingsCount) }}
                </button>
              </div>

              <!-- Toggle pour changer le mode d'affichage (mobile) -->
              <ViewModeToggleButton v-if="!isDesktop"
                                    v-model="viewMode" />
            </template>
            <div v-else-if="!getBuildingsByProjectIdLoading"
                 class="placeholderNoInformation">
              {{ t('browsingApp.buildings.noBuildings') }}
            </div>
          </template>
          <template v-if="!getProjectRenderCanvasLoading" #canvas>
            <template v-if="buildingLayers?.length">
              <ProjectRenderCanvasAccessible v-show="canvasIsReady"
                                             v-model:hovered-building="hoveredBuilding"
                                             :building-layers="buildingLayers"
                                             :buildings="allBuildings"
                                             :project="project"
                                             :hovered-unit="hoveredUnit"
                                             @canvas-loaded="onCanvasLoaded"
                                             @building-click="onLayerClick"
                                             flow-type="rental" />
              <LoadingSpinner v-show="!canvasIsReady" />
            </template>
            <div v-else
                 class="placeholderNoInformation">
              {{ t('browsingApp.projects.noProjectCanvasData') }}
            </div>
          </template>
        </CanvasDataPanels>

        <BuildingCanvasDialogModal v-model:open="showBuildingModal"
                                   :building="modalBuilding"
                                   @building-selected="onBuildingSelected"
                                   @reset-building-modal="resetDialogState" />

        <!-- Modal de notification pour les bâtiments de vente -->
        <DialogModal v-model:open="showPurchaseNotificationModal"
                     :show-footer="true"
                     :confirm-button-label="t('browsingApp.actions.continueToPurchase')"
                     :cancel-button-label="t('browsingApp.actions.cancel')"
                     @confirm="onConfirmPurchaseRedirect"
                     @close="onCancelPurchaseRedirect">
          <template #headerContent>
            <h2>{{ t('browsingApp.purchase.purchaseBuildingNotice') }}</h2>
          </template>
          
          <div class="purchaseNotification-content">
            <p>{{ t('browsingApp.purchase.purchaseRedirectMessage', { buildingNumber: purchaseBuilding?.no }) }}</p>
          </div>
        </DialogModal>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.browsingApp_projectRentalBrowsing {
  [data-status="Available"] {
    --baseStatusColor: var(--clrGreenStatus);
  }

  [data-status="Reserved"] {
    --baseStatusColor: var(--clrOrangeStatus);
  }

  [data-status="Upcoming"] {
    --baseStatusColor: var(--clrYellowStatus);
  }

  [data-status="Sold"],
  [data-status="Full"] {
    --baseStatusColor: var(--clrRedStatus);
  }

  [data-status="Rental"] {
    --baseStatusColor: var(--clrPurpleStatus);
  }

  // Bâtiments de vente (Purchase) apparaissent en violet dans la vue Location
  .renderCanvas_project [data-listing-type="Sale"] {
    --baseColor: var(--clrPurpleStatus) !important;
  }
}
.loadMoreBuildings {
  display: flex;
  justify-content: center;
  margin: var(--spc48-24) 0;

  .btn {
    background-color: #28a745;
    border-color: #28a745;
    color: white;

    &:hover {
      background-color: #218838;
      border-color: #1e7e34;
    }

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
  }
}
</style>